/* iter55z-b — Landing page "community activity" surface.
 *
 * Two pieces exported here:
 *   - `<ActivityPill>` — a small live count that renders next to the
 *     hero eyebrow ("24 writers active right now").
 *   - `<ActivityTicker>` — an anonymized carousel of recent forum
 *     posts, rendered above the Bookstore card as social proof.
 *
 * Both hit `GET /api/public/community/activity` (unauth). We refresh
 * every 45 s so a visitor lingering on the hero sees the number tick.
 *
 * The endpoint returns:
 *   { active_now, total_writers, recent_posts: [{ first_name, snippet, topic, at }] }
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Users, Sparkles } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const REFRESH_MS = 45_000;

function useActivity() {
  const [data, setData] = useState(null);
  const alive = useRef(true);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/community/activity`);
      if (alive.current) setData(r.data);
    } catch {
      /* keep whatever we had */
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    load();
    const iv = window.setInterval(load, REFRESH_MS);
    return () => { alive.current = false; window.clearInterval(iv); };
  }, [load]);
  return data;
}

function relTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, (Date.now() - t) / 1000);
    if (s < 60)   return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

/* ----------------------------- Pill ----------------------------- */

export function ActivityPill({ accent, cardBg, borderSoft }) {
  const { t } = useTranslation();
  const data = useActivity();
  if (!data) return null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold tracking-normal"
      style={{ borderColor: borderSoft, backgroundColor: cardBg, color: accent }}
      data-testid="landing-activity-pill"
      title={`${data.total_writers.toLocaleString()}+ writers registered`}
    >
      <span
        className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
        style={{ backgroundColor: accent }}
      />
      {t('landing.activityPillCount', { count: data.active_now })}
    </span>
  );
}

/* ---------------------------- Ticker ---------------------------- */

export function ActivityTicker({ accent, cardBg, borderSoft }) {
  const { t } = useTranslation();
  const data = useActivity();
  const [idx, setIdx] = useState(0);

  // Rotate every 6 seconds.
  useEffect(() => {
    if (!data?.recent_posts?.length) return undefined;
    const iv = window.setInterval(() => {
      setIdx((prev) => (prev + 1) % data.recent_posts.length);
    }, 6_000);
    return () => window.clearInterval(iv);
  }, [data]);

  if (!data?.recent_posts?.length) return null;
  const post = data.recent_posts[idx] || data.recent_posts[0];

  return (
    <section
      className="mx-auto max-w-6xl px-6 pb-4"
      data-testid="landing-activity-ticker"
      aria-label="Recent community activity"
    >
      <div
        className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center"
        style={{ borderColor: borderSoft, backgroundColor: cardBg }}
      >
        <div
          className="inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.2em]"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Users className="h-3 w-3" /> {t('landing.activityLiveLabel')}
        </div>
        <div className="flex-1 overflow-hidden">
          <div key={post.at} className="animate-fadeIn text-[13px]">
            <b className="font-semibold">{post.first_name}</b>
            <span className="opacity-60"> · in {post.topic || 'community'} · {relTime(post.at)}</span>
            <p className="mt-0.5 line-clamp-2 opacity-90">“{post.snippet}”</p>
          </div>
        </div>
        <a
          href="/community"
          className="shrink-0 text-[11.5px] font-semibold hover:underline"
          style={{ color: accent }}
          data-testid="landing-activity-ticker-cta"
        >
          {t('landing.activityJoinCta')} <Sparkles className="ml-1 inline h-3 w-3" />
        </a>
      </div>
    </section>
  );
}
